import React, { useMemo, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Card from '../../ui/Card.jsx';
import Legend from './Legend.jsx';
import { getActiveTheme, resolveThemeName } from '../../ui/tokens';
import { formatMinutesToHM } from '../../ui/format';

function formatX(d) {
  // expects YYYY-MM-DD -> DD/MM
  if (!d) return '';
  const [y,m,dd] = String(d).split('-');
  return `${dd}/${m}`;
}

function MinutesTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="text-xs rounded border bg-white dark:bg-gray-900 p-2">
      <div className="font-medium mb-1">{label}</div>
      {payload.map((p,i)=>(
        <div key={i} className="flex justify-between gap-4">
          <span className="text-gray-600 dark:text-gray-300">{p.name}</span>
          <span>{formatMinutesToHM(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

function isWeekendYmd(ymd) {
  if (!ymd) return false;
  // Use UTC midday to avoid timezone/DST edge cases.
  const d = new Date(String(ymd) + 'T12:00:00Z');
  const dow = d.getUTCDay(); // 0=Sun..6=Sat
  return dow === 0 || dow === 6;
}

function formatMinutesAsHoursTick(value) {
  const minutes = Number(value);
  if (!Number.isFinite(minutes)) return '';

  const hours = minutes / 60;
  const rounded = Math.abs(hours) >= 10 ? Math.round(hours) : Math.round(hours * 10) / 10;
  return `${rounded}h`;
}

export default function HoursTrendChart({ data = [], onDayClick, loading = false }) {
  const theme = getActiveTheme();
  const isPremiumLight = resolveThemeName() === 'auroraExecutiveLight' || resolveThemeName() === 'redLightPremium';
  const gridOpacity = Number(theme?.chartGridOpacity ?? (isPremiumLight ? 0.06 : 0.12));
  const gridStroke = isPremiumLight ? `rgba(15, 23, 42, ${gridOpacity})` : (theme?.border || '#e5e7eb');
  const axisStroke = isPremiumLight ? 'rgba(15, 23, 42, 0.14)' : (theme?.border || '#e5e7eb');
  const [showPlanned, setShowPlanned] = useState(true);
  const [showTotal, setShowTotal] = useState(false);
  const [showWeekends, setShowWeekends] = useState(false);

  const visibleData = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    if (showWeekends) return rows;
    return rows.filter(d => !isWeekendYmd(d?.date));
  }, [data, showWeekends]);

  const total = (visibleData || []).reduce((acc, d) => acc + Number(d.planned_minutes||0) + Number(d.logged_total_minutes||0), 0);
  const baseSeries = useMemo(() => ([
    { key: 'logged_assigned_minutes', name: 'Logged Planned', color: theme.primary },
    { key: 'logged_other_minutes', name: 'Logged Other', color: theme.warning },
  ]), [theme.primary, theme.warning]);
  const optionalSeries = useMemo(() => ([
    ...(showPlanned ? [{ key: 'planned_minutes', name: 'Planned', color: theme.success }] : []),
    ...(showTotal ? [{ key: 'logged_total_minutes', name: 'Logged Total', color: theme.text }] : []),
  ]), [showPlanned, showTotal, theme.success, theme.text]);
  const series = [...baseSeries, ...optionalSeries];
  const activeSeries = series.filter(s => (visibleData||[]).some(d => Number(d[s.key]||0) > 0));
  return (
    <Card padded={true} className="w-full">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold">Hours trend (by day)</div>
          <div className="flex items-center gap-1 text-xs">
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={showPlanned} onChange={e=> setShowPlanned(e.target.checked)} />
              Show planned
            </label>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={showTotal} onChange={e=> setShowTotal(e.target.checked)} />
              Show total
            </label>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={showWeekends} onChange={e=> setShowWeekends(e.target.checked)} />
              Show weekends
            </label>
          </div>
        </div>
        <Legend items={activeSeries.map(s => ({ label: s.name, color: s.color }))} />
      </div>
      <div className="h-[220px]">
        {loading && (<div className="absolute inset-0 bg-black/5 dark:bg-white/5 flex items-center justify-center text-xs">Loading…</div>)}
        {(!visibleData || visibleData.length===0 || total===0) ? (
          <div className="h-full flex items-center justify-center text-xs text-gray-600 dark:text-gray-400">No data for selected range</div>
        ) : (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={visibleData} onClick={(e)=>{
            // e.activeLabel is the date (YYYY-MM-DD) if a point was clicked
            if (e && e.activeLabel && onDayClick) onDayClick(e.activeLabel);
          }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis
              dataKey="date"
              tickFormatter={formatX}
              tick={isPremiumLight ? { fill: theme?.mutedText || '#6B7280', fontSize: 12 } : undefined}
              axisLine={isPremiumLight ? { stroke: axisStroke } : undefined}
              tickLine={isPremiumLight ? { stroke: axisStroke } : undefined}
            />
            <YAxis
              tickFormatter={formatMinutesAsHoursTick}
              tick={isPremiumLight ? { fill: theme?.mutedText || '#6B7280', fontSize: 12 } : undefined}
              axisLine={isPremiumLight ? { stroke: axisStroke } : undefined}
              tickLine={isPremiumLight ? { stroke: axisStroke } : undefined}
            />
            <Tooltip content={<MinutesTooltip />} />
            {series.map(s => (
              <Area key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color} fill={s.color} fillOpacity={0.15} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}
